import {useEffect, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Loader2, X} from '@digital-twins/design-system/icons'
import type {StudyArtifactAttentionTarget, StudyFindingsView} from '@digital-twins/shared-types'
import {MarkdownBody} from '@design-system/components/MarkdownBody'
import {Button} from '@/components/ui/button'
import {SavedFindingsQuestions} from './SavedFindingsQuestions'
import {ThreadFeaturedExhibits} from '@/components/quick-study-results/featuredPlots/ThreadFeaturedExhibits'
import {sameStudyArtifactTarget, studyFindingsApi} from '@/lib/api/study-findings'

interface Props {
  threadToken: string
  studyToken: string
  target?: StudyArtifactAttentionTarget | null
  onRendered: (target: StudyArtifactAttentionTarget) => void
  onPost: (target: StudyArtifactAttentionTarget) => Promise<unknown>
  onClose: () => void
  /** The study is a Quick Study: its plots print one decimal. */
  isQuickStudy?: boolean
}

/** Exact saved findings in the existing session side panel, with no new writer. */
export function ThreadStudyFindings({threadToken, studyToken, target, onRendered, onPost, onClose, isQuickStudy = false}: Props) {
  const {t} = useTranslation('app')
  const [data, setData] = useState<StudyFindingsView | null>(null)
  const [error, setError] = useState(false)
  const [attempt, setAttempt] = useState(0)
  const [posting, setPosting] = useState(false)
  const [posted, setPosted] = useState(false)
  const rendered = useRef(onRendered)
  rendered.current = onRendered
  const acknowledged = useRef('')
  useEffect(() => {
    let active = true
    setData(null); setError(false)
    void studyFindingsApi.read(threadToken, studyToken, target?.artifactRevision).then((result) => {
      if (!active) return
      if (result.target.threadToken !== threadToken || result.target.studyToken !== studyToken ||
        (target && !sameStudyArtifactTarget(target, result.target))) throw new Error('Unexpected saved findings target')
      setData(result)
    }).catch(() => {if (active) setError(true)})
    return () => {active = false}
  }, [threadToken, studyToken, target?.artifactRevision, target?.artifactToken, target?.originMessageToken, attempt])

  useEffect(() => {
    if (!data) return
    const identity = `${data.target.artifactToken}:${data.target.artifactRevision}`
    if (acknowledged.current === identity) return
    acknowledged.current = identity
    // Effects run after this exact body and its existing plots enter the DOM.
    rendered.current(data.target)
  }, [data])

  async function post() {
    if (!data || posting) return
    setPosting(true)
    try {if (await onPost(data.target) === true) setPosted(true)} finally {setPosting(false)}
  }
  return <section className="flex h-full min-h-0 flex-col bg-background text-foreground" data-testid="thread-study-findings" data-artifact-revision={data?.target.artifactRevision}>
    <header className="flex shrink-0 items-center justify-between gap-3 border-b border-border px-4 py-3">
      <h2 className="text-sm font-medium">{data?.title || t('threads.quickStudy.findingsTitle')}</h2>
      <Button variant="ghost" size="icon" aria-label={t('threads.quickStudy.close')} onClick={onClose}><X className="h-4 w-4" aria-hidden /></Button>
    </header>
    <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
      {error ? <div role="alert" className="space-y-3 text-sm"><p>{t('threads.quickStudy.resultsFailed')}</p><Button variant="outline" onClick={() => setAttempt((value) => value + 1)}>{t('threads.quickStudy.retry')}</Button></div>
        : !data ? <div role="status" className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" aria-hidden />{t('threads.quickStudy.loadingResults')}</div>
        : <>{data.questions?.length ? <SavedFindingsQuestions questions={data.questions} onRetry={() => setAttempt((value) => value + 1)} isQuickStudy={isQuickStudy} /> : <><MarkdownBody content={data.body} density="thread" /><ThreadFeaturedExhibits charts={data.charts} tables={data.tables} /></>}
          {data.canPost ? <Button variant="outline" disabled={posting || posted} onClick={() => void post()}>{posted ? t('threads.quickStudy.posted') : posting ? t('threads.quickStudy.posting') : t('threads.quickStudy.postResults')}</Button> : null}</>}
    </div>
  </section>
}
